import { ColliClient } from '@frontend/colli/api';
import { Colli } from '@frontend/colli/types';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageType } from '@frontend/package/types';
import { TransactionExtendedClient } from '@frontend/transaction/api';
import { ExtendedTransaction } from '@frontend/transaction/types';

const CLOSED_DROP_OFF_STATES = new Set([
    PackageStateName.PACKAGE_DROP_OFF_DONE,
    PackageStateName.PACKAGE_DROP_OFF_CANCELLED,
    PackageStateName.PACKAGE_DROP_OFF_SKIPPED
]);

export function isOpenableDropOffPackage(pkg: Package): boolean {
    return pkg.type === PackageType.DROP_OFF && !CLOSED_DROP_OFF_STATES.has(pkg.state);
}

/**
 * Openable DROP_OFF packages for a transaction at the current SPOT,
 * optionally limited to specific collis.
 */
export async function fetchOpenableDropOffPackages(args: {
    accountId: string;
    transactionId: string;
    spotId?: string | null;
    colliIds?: string[];
}): Promise<Package[]> {
    const response = await PackageClient.fetchPackages({
        account_id: args.accountId,
        transaction_id: args.transactionId,
        spot_id: args.spotId ?? undefined,
        type: PackageType.DROP_OFF
    });

    let packages = response.results.filter(isOpenableDropOffPackage);

    if (args.colliIds?.length) {
        const colliIds = new Set(args.colliIds);
        packages = packages.filter((pkg) => pkg.colli_id != null && colliIds.has(pkg.colli_id));
    }

    return packages.sort((a, b) => a.priority - b.priority);
}

/**
 * Resolves transaction + openable drop-off packages for an identified colli at the current SPOT.
 */
export async function resolveDropOffColliContext(args: {
    accountId: string;
    spotId?: string | null;
    colli: Colli;
}): Promise<{ transaction: ExtendedTransaction; collis: Colli[]; packages: Package[] }> {
    if (!args.colli.transaction_id) {
        throw new Error('Colli has no transaction_id');
    }

    const transaction = await TransactionExtendedClient.fetchExtendedTransaction(args.accountId, args.colli.transaction_id);
    const packages = await fetchOpenableDropOffPackages({
        accountId: args.accountId,
        transactionId: args.colli.transaction_id,
        spotId: args.spotId,
        colliIds: [args.colli.id]
    });

    return { transaction, collis: [args.colli], packages };
}

/**
 * Resolves wizard drop-off context from an MQTT/API identified package
 * (e.g. `package_inform_iot_identified` after pin/QR).
 */
export async function resolveDropOffPackageContext(args: {
    accountId: string;
    spotId?: string | null;
    package: Package;
}): Promise<{ transaction: ExtendedTransaction; collis: Colli[]; packages: Package[] }> {
    if (!args.package.transaction_id) {
        throw new Error('Package has no transaction_id');
    }

    if (args.package.colli_id) {
        const colliResult = await ColliClient.resolveCollis([args.package.colli_id]);
        const colli = colliResult.results[0];
        if (colli) {
            const context = await resolveDropOffColliContext({
                accountId: args.accountId,
                spotId: args.spotId,
                colli
            });
            if (context.packages.length > 0) return context;

            // Keep the identified package if the SPOT filter returned nothing.
            return {
                ...context,
                packages: isOpenableDropOffPackage(args.package) ? [args.package] : []
            };
        }
    }

    const transaction = await TransactionExtendedClient.fetchExtendedTransaction(args.accountId, args.package.transaction_id);
    let packages = await fetchOpenableDropOffPackages({
        accountId: args.accountId,
        transactionId: args.package.transaction_id,
        spotId: args.spotId
    });

    if (packages.length === 0 && isOpenableDropOffPackage(args.package)) {
        packages = [args.package];
    }

    return { transaction, collis: [], packages };
}
